import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { EarlyPayoutConfig, MyBetsVisualizationTrackingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { EarlyPayoutErrors } from '@frontend/sports/types/components/sitecore';

import { GeolocationCheckService } from '../../../../geo-location/geo-location-check.service';
import { EarlyPayoutHelpers } from '../../../../my-bets-base/early-payout/my-bets-earlypayout-helpers';
import {
    AutoCashoutResponse,
    AutoCashoutState,
    CashoutSettingsBetInfo,
    CashoutSettingsType,
    IAutoCashoutBetslipNumbers,
    SetAutoCashoutRequest,
} from '../../../../my-bets-base/models/early-payout';
import { AutoCashoutTrackingService } from '../../../services/auto-cash-out-tracking-service';
import { AutoCashoutService } from '../../../services/auto-cash-out.service';
import { CashoutSettingsService } from '../../../services/cash-out-settings.service';
import { MyBetsStrategy } from '../../../services/my-bets-display.strategy';
import { MyBetsEarlyPayoutAutocashoutValidationService } from './my-bets-earlypayout-autocashout-validation.service';

@Component({
    selector: 'ms-my-bets-earlypayout-autocashout-button',
    templateUrl: 'my-bets-earlypayout-autocashout-button.html',
})
export class MyBetsEarlyPayoutAutocashoutButtonComponent implements OnInit {
    @Input() value = 0;
    @Input() setType: CashoutSettingsType;
    @Input() btnerror: string | null = null;
    @Input() betNumbers: IAutoCashoutBetslipNumbers;
    @Input() betTypeInfo: string;
    @Output() btnerrorChange: EventEmitter<string | null> = new EventEmitter<string | null>();

    state: AutoCashoutState;
    btnText: string;
    private betInfo: CashoutSettingsBetInfo | null;

    constructor(
        public sitecore: Sitecore,
        private autoCashoutService: AutoCashoutService,
        private epConfig: EarlyPayoutConfig,
        private epHelpers: EarlyPayoutHelpers,
        private cashoutSettingsService: CashoutSettingsService,
        private autoCashoutValidationService: MyBetsEarlyPayoutAutocashoutValidationService,
        private autoCashoutTrackingService: AutoCashoutTrackingService,
        private myBetsStrategy: MyBetsStrategy,
        private geolocationCheckService: GeolocationCheckService,
        private mybetsVisualizationTrackingConfig: MyBetsVisualizationTrackingConfig,
    ) {
        this.autoCashoutTrackingService.myBetsTrackSufix = this.myBetsStrategy.inColumn ? 'Mini_MyBets' : 'Main_MyBets';
    }

    ngOnInit(): void {
        this.betInfo = this.cashoutSettingsService.getBetInfo(this.betNumbers.betslipId);
        this.setState(AutoCashoutState.Available);

        if (!this.mybetsVisualizationTrackingConfig.isEnabled) {
            this.betTypeInfo = this.betInfo?.betType ?? '';
        }
    }

    async submitAutoCashout(): Promise<void> {
        if (this.state === AutoCashoutState.Processing) {
            return;
        }

        const validation = this.autoCashoutValidationService.isValueValid(this.value, this.betInfo);

        if (!validation.valid) {
            this.btnerror = validation.errorMessage;
            this.btnerrorChange.emit(this.btnerror);

            return;
        }

        this.btnerror = null;
        this.btnerrorChange.emit(this.btnerror);

        if (!this.betInfo || !this.betInfo.earlyPayout || !this.epConfig.isAutoCashOutEnabled) {
            return;
        }

        // Always send gross (stake included) autoCashoutValue to backend
        const valueWithStake = this.epHelpers.getValueWithFreeBetStake(this.betInfo.stake.value, this.value, this.betInfo.isFreeBet);
        const request: SetAutoCashoutRequest = {
            betNumber: this.betNumbers.betslipRealId,
            autoCashoutValue: valueWithStake || this.value,
            autoCashoutType: Number(this.setType),
        };
        this.setState(AutoCashoutState.Processing);

        // Send user original input (stake excluded) to web tracking
        this.autoCashoutTrackingService.trackSetClick(
            this.setType,
            this.betInfo.betslipId,
            this.value,
            this.betTypeInfo,
            this.betInfo.isFreeBet,
            this.betInfo.containsLiveBets,
        );

        this.geolocationCheckService.checkGeolocation(
            (isLocationFailed) => {
                if (isLocationFailed) {
                    this.setState(AutoCashoutState.Available);

                    return;
                }

                return this.setEarlyPayoutAmount(request);
            },
            { waitForLocationAvailable: true },
        );
    }

    async setEarlyPayoutAmount(request: SetAutoCashoutRequest): Promise<void> {
        try {
            const response = await this.autoCashoutService.setEarlyPayoutAmount(request);
            this.handleAutoCashoutSuccess(response, request.autoCashoutValue);
        } catch {
            this.handleAutoCashoutErrors();
        }
    }

    private handleAutoCashoutSuccess(response: AutoCashoutResponse | null, autoCashoutValue: number): void {
        if (!response || !response.setAutoCashoutErrors || response.setAutoCashoutErrors.length) {
            this.handleAutoCashoutErrors(response);

            return;
        }
        this.setState(AutoCashoutState.Available);
        this.cashoutSettingsService.updateAutoCashOutSettings(this.betNumbers.betslipId, autoCashoutValue, this.setType);

        if (!this.betInfo || !this.betInfo.earlyPayout || !this.epConfig.isAutoCashOutEnabled) {
            return;
        }

        const valueWithoutStake =
            this.setType === CashoutSettingsType.Auto
                ? this.betInfo.earlyPayout.autoCashOutValueWithoutStake
                : this.betInfo.earlyPayout.autoCashoutNotificationValueWithoutStake;

        this.autoCashoutTrackingService.trackSetSuccess(
            this.setType,
            this.betNumbers.betslipId,
            valueWithoutStake || autoCashoutValue,
            this.betTypeInfo,
            this.betInfo.isFreeBet,
            this.betInfo.containsLiveBets,
        );
    }

    private handleAutoCashoutErrors(response: AutoCashoutResponse | null = null): void {
        const payoutError = response?.setAutoCashoutErrors.find(
            (error) =>
                error.type === 'ParallelGamePlayPrevention' ||
                error.type === 'PlayerNotRegisteredWithCFR' ||
                error.type === 'UserIsSelfExcluded' ||
                error.type === 'OasisDown',
        );
        if (payoutError) {
            const pushError = this.sitecore.earlyPayoutPushErrors.find((error) => error.name === payoutError.type.toLowerCase());
            this.btnerror = pushError?.error.Message || this.sitecore.earlyPayoutErrors[payoutError.type as keyof EarlyPayoutErrors];
        } else {
            this.btnerror =
                this.setType === CashoutSettingsType.Auto
                    ? this.sitecore.earlyPayoutErrors.AutoCashoutGeneralError
                    : this.sitecore.earlyPayoutErrors.AutoCashoutNotificationGeneralError;
        }
        this.btnerrorChange.emit(this.btnerror);
        this.setState(AutoCashoutState.Available);
    }

    private setState(state: AutoCashoutState): void {
        switch (state) {
            case AutoCashoutState.Available:
                this.btnText =
                    this.setType === CashoutSettingsType.Auto
                        ? this.sitecore.earlyPayoutV2.SetAutocashoutButtonSetText
                        : this.sitecore.earlyPayoutV2.SetAutocashoutNotificationButtonSetText;
                break;
            case AutoCashoutState.Processing:
                this.btnText = this.sitecore.earlyPayoutV2.SetAutocashoutButtonInProgressText;
                break;
        }

        this.state = state;
    }
}
